import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { CheckCircle2, Printer, QrCode, XCircle } from "lucide-react";
import { SUPPORTED_LANGUAGES, buildScanUrl } from "@wedding/shared";
import {
  MAX_TABLE_PAGES, PRINT_TEMPLATES, dateDots, parseTables, splitCouple,
  type PrintCopy, type PrintCtx, type PrintDoc, type TemplateId,
} from "./templates";

/**
 * "Baskı" — düğüne özel masa kartı / karşılama panosu / el kartı.
 * Organizatör ve admin AYNI bileşeni ince adaptörle render eder
 * (Admin ⇄ Organizer eşitliği); veri tamamen istemcide üretilir, sunucu yok.
 *
 * QR her zaman `buildScanUrl(webBase, slug)` → `/j/:slug` (tarama sayılır,
 * kayıt sayfasına yönlenir — kritik yol 3). Kart metinleri DÜĞÜNÜN dilinde.
 */

const MM_TO_PX = 96 / 25.4;

const COPY_KEYS: (keyof PrintCopy)[] = [
  "shareEyebrow", "step1", "step2", "step3", "table", "welcome", "headlineA", "headlineB",
  "captionStrong", "caption", "noApp", "handEyebrow", "handLine1", "handLine2", "handSteps",
];

const META: Record<TemplateId, { name: string; size: string }> = {
  "table-card": { name: "printKit.tableCard", size: "printKit.tableCardSize" },
  "welcome-sign": { name: "printKit.welcomeSign", size: "printKit.welcomeSignSize" },
  "hand-card": { name: "printKit.handCard", size: "printKit.handCardSize" },
};

export interface PrintKitWedding {
  /** Yanıtta eksik gelebilir (eski kayıt / kısmi yük) — bileşen çökmemeli. */
  title?: string | null;
  date_time: string | null;
  config?: { language?: unknown } | null;
}

/** Belgeyi gizli bir iframe'de açıp yazdırma penceresini çağırır. */
function printDoc(d: PrintDoc): Promise<void> {
  return new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    frame.onload = async () => {
      try {
        const w = frame.contentWindow;
        if (!w) throw new Error("no_window");
        // Fontlar gelmeden yazdırılırsa yedek yazı tipine düşer — en fazla 3 sn bekle.
        await Promise.race([w.document.fonts?.ready, new Promise((r) => setTimeout(r, 3000))]);
        w.focus();
        w.print();
        resolve();
      } catch (err) {
        reject(err);
      } finally {
        setTimeout(() => frame.remove(), 60_000);
      }
    };
    frame.srcdoc = d.html;
    document.body.appendChild(frame);
  });
}

export function PrintKitPage({
  slug,
  wedding,
  webBaseUrl,
  onNotify,
}: {
  slug: string;
  wedding: PrintKitWedding | null;
  webBaseUrl: string;
  onNotify?: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const rawLang = typeof wedding?.config?.language === "string" ? wedding.config.language : "tr";
  const lang = SUPPORTED_LANGUAGES.some((l) => l.code === rawLang) ? rawLang : "tr";
  const tw = useMemo(() => i18n.getFixedT(lang), [i18n, lang]);

  const [initialA, initialB] = splitCouple(wedding?.title);
  const [nameA, setNameA] = useState(initialA);
  const [nameB, setNameB] = useState(initialB);
  const [date, setDate] = useState(dateDots(wedding?.date_time));
  const [tablesInput, setTablesInput] = useState("1-10");
  const [qrSvg, setQrSvg] = useState("");
  // Uygulama kendi toast'unu vermezse paketin küçük toast'u (her eylem bildirilir).
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const notify = onNotify ?? ((msg: string, ok = true) => setToast({ msg, ok }));
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  // Düğün bilgisi geç gelirse alanları bir kez doldur.
  useEffect(() => {
    if (!wedding) return;
    const [a, b] = splitCouple(wedding.title);
    setNameA((v) => v || a);
    setNameB((v) => v || b);
    setDate((v) => v || dateDots(wedding.date_time));
  }, [wedding]);

  const scanUrl = buildScanUrl(webBaseUrl, slug);
  useEffect(() => {
    let alive = true;
    QRCode.toString(scanUrl, { type: "svg", margin: 0, errorCorrectionLevel: "M", color: { dark: "#1a1816ff", light: "#ffffffff" } })
      .then((svg) => alive && setQrSvg(svg))
      .catch(() => alive && setQrSvg(""));
    return () => {
      alive = false;
    };
  }, [scanUrl]);

  const ctx: PrintCtx = useMemo(
    () => ({
      nameA,
      nameB,
      dateDots: date,
      qrSvg,
      tables: parseTables(tablesInput),
      domain: webBaseUrl.replace(/^https?:\/\//, "").replace(/\/.*$/, ""),
      copy: Object.fromEntries(COPY_KEYS.map((k) => [k, tw(`printKit.tpl.${k}`)])) as unknown as PrintCopy,
    }),
    [nameA, nameB, date, qrSvg, tablesInput, webBaseUrl, tw],
  );

  const docs = useMemo(() => PRINT_TEMPLATES.map((tpl) => ({ id: tpl.id, doc: tpl.build(ctx, lang) })), [ctx, lang]);

  const onPrint = async (d: PrintDoc) => {
    try {
      await printDoc(d);
      notify(t("printKit.printing"));
    } catch {
      notify(t("printKit.printFailed"), false);
    }
  };

  const field =
    "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-serif font-bold text-foreground flex items-center gap-2">
          <Printer className="h-6 w-6 text-primary" aria-hidden="true" />
          {t("printKit.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t("printKit.description")}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1.5 text-xs font-medium text-foreground">
          {t("printKit.nameA")}
          <input value={nameA} maxLength={40} onChange={(e) => setNameA(e.target.value)} className={field} />
        </label>
        <label className="space-y-1.5 text-xs font-medium text-foreground">
          {t("printKit.nameB")}
          <input value={nameB} maxLength={40} onChange={(e) => setNameB(e.target.value)} className={field} />
        </label>
        <label className="space-y-1.5 text-xs font-medium text-foreground">
          {t("printKit.date")}
          <input value={date} maxLength={40} onChange={(e) => setDate(e.target.value)} className={field} />
        </label>
        <label className="space-y-1.5 text-xs font-medium text-foreground">
          {t("printKit.tables")}
          <input value={tablesInput} maxLength={200} onChange={(e) => setTablesInput(e.target.value)} className={field} />
          <span className="block text-[11px] font-normal text-muted-foreground">{t("printKit.tablesHint", { max: MAX_TABLE_PAGES })}</span>
        </label>
        <p className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <QrCode className="h-3.5 w-3.5" aria-hidden="true" />
          {t("printKit.qrTarget")}{" "}
          <code data-testid="print-scan-url" className="rounded bg-muted px-1.5 py-0.5 text-foreground">{scanUrl}</code>
          <span>· {t("printKit.langNote", { lang: lang.toUpperCase() })}</span>
        </p>
      </div>

      <ul className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {docs.map(({ id, doc }) => (
          <li key={id} data-testid={`print-${id}`} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
            <Preview doc={doc} title={t(META[id].name)} />
            <div className="flex flex-1 flex-wrap items-end justify-between gap-3 border-t border-border px-5 py-4">
              <div>
                <p className="text-sm font-semibold text-foreground">{t(META[id].name)}</p>
                <p className="text-xs text-muted-foreground">
                  {t(META[id].size)}
                  {doc.pages > 1 ? ` · ${t("printKit.pages", { count: doc.pages })}` : ""}
                </p>
              </div>
              <button
                type="button"
                disabled={!qrSvg}
                onClick={() => void onPrint(doc)}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <Printer className="h-4 w-4" aria-hidden="true" />
                {t("printKit.print")}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted-foreground">{t("printKit.tip")}</p>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-6 left-1/2 z-[200] -translate-x-1/2 flex items-center gap-2.5 rounded-2xl px-4 py-3 text-sm font-medium shadow-xl text-white ${toast.ok ? "bg-emerald-500" : "bg-red-500"}`}
        >
          {toast.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/** İlk sayfanın küçültülmüş canlı önizlemesi (gerçek ölçü → kutuya sığdırılır). */
function Preview({ doc, title }: { doc: PrintDoc; title: string }) {
  const BOX = 280;
  const wPx = doc.widthMm * MM_TO_PX;
  const hPx = doc.heightMm * MM_TO_PX;
  const scale = Math.min(BOX / wPx, BOX / hPx);
  return (
    <div className="flex items-center justify-center bg-muted/40 p-5" style={{ height: BOX + 40 }}>
      <div className="overflow-hidden rounded-md shadow-lg ring-1 ring-black/5" style={{ width: wPx * scale, height: hPx * scale }}>
        <iframe
          title={title}
          srcDoc={doc.html}
          sandbox=""
          tabIndex={-1}
          aria-hidden="true"
          style={{ width: wPx, height: hPx, border: 0, transform: `scale(${scale})`, transformOrigin: "0 0", pointerEvents: "none" }}
        />
      </div>
    </div>
  );
}
